import { createBrowserRouter } from 'react-router-dom';

import { ensureNamespaces } from '@/lib/i18n/catalog-store.js';
import { PAGE_NAMESPACES } from '@/lib/i18n/namespaces.js';

import { GuestOnlyRoute } from './guards.js';
import { HomeRoute } from './HomeRoute.js';
import { NotFoundPage } from './NotFoundPage.js';
import { RootLayout } from './RootLayout.js';
import { ROUTES } from './routes.js';

/**
 * Дерево маршрутов.
 *
 * ⚠️ Ни один путь не может начинаться с /api, /payment, /public или /go: это
 * RESERVED_PREFIXES сервера (server/lib/http/reserved-paths.ts, заморожено тестами).
 * Запросы туда обрабатывает Express и до index.html они не доходят — маршрут здесь
 * просто никогда бы не сработал. Именно поэтому результат оплаты живёт на
 * /payment-result (вне сегмента /payment/), а не на /payment/success из таблицы prompt_07.
 *
 * Страницы грузятся лениво (`lazy`), а не импортируются сверху: в волне 07 кабинет
 * потянет за собой KaTeX и рендер решений, и этот вес не должен попадать в начальный
 * бандл, который видит гость на лендинге.
 *
 * ⚠️ Вместе с чанком каждый маршрут ждёт СВОИ namespace каталога (lib/i18n/namespaces.ts).
 * Это единственное место, где их можно дождаться: компоненты читают каталог синхронно,
 * значит к первому рендеру строки обязаны уже лежать в сторе. Оба ожидания идут через
 * Promise.all — сеть за чанком и сеть за строками стартуют одновременно.
 *
 * Добавляете страницу — добавьте и её набор в PAGE_NAMESPACES. Забудете: в разработке
 * страж каталога бросит «missing key …» на первом же обращении, молчаливой пустоты
 * в вёрстке не будет.
 *
 * Guard'ы навешаны маршрутами-обёртками, а не проверками внутри страниц: так правило
 * доступа видно в одном месте и его нельзя забыть на новой странице.
 */
export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // `/` без guard'а: он сам решает, лендинг это или кабинет, и делает это без редиректа.
      { path: ROUTES.home, element: <HomeRoute /> },

      {
        element: <GuestOnlyRoute />,
        children: [
          {
            path: ROUTES.signin,
            lazy: async () => {
              const [module] = await Promise.all([
                import('@/features/auth/SignInPage.js'),
                ensureNamespaces(PAGE_NAMESPACES.auth),
              ]);
              return { Component: module.SignInPage };
            },
          },
          {
            /** Регистрация — свой экран (артборд 5a), а не режим страницы входа. */
            path: ROUTES.register,
            lazy: async () => {
              const [module] = await Promise.all([
                import('@/features/auth/RegisterPage.js'),
                ensureNamespaces(PAGE_NAMESPACES.auth),
              ]);
              return { Component: module.RegisterPage };
            },
          },
          {
            // Токен в query-строке (/reset?token=...), а не сегментом пути:
            // так его формирует server/lib/mail/links.ts:35.
            path: ROUTES.reset,
            lazy: async () => {
              const [module] = await Promise.all([
                import('@/features/auth/ResetPasswordPage.js'),
                ensureNamespaces(PAGE_NAMESPACES.auth),
              ]);
              return { Component: module.ResetPasswordPage };
            },
          },
        ],
      },

      {
        // Результат подтверждения почты доступен и гостю, и авторизованному:
        // GET /auth/verify намеренно НЕ логинит пользователя, он только редиректит сюда.
        path: ROUTES.verify,
        lazy: async () => {
          const [module] = await Promise.all([
            import('@/features/auth/VerifyResultPage.js'),
            ensureNamespaces(PAGE_NAMESPACES.auth),
          ]);
          return { Component: module.VerifyResultPage };
        },
      },
      {
        path: ROUTES.support,
        // Странице контактов хватает ядра каталога: её тексты лежат в `support`, а он
        // в CORE_NAMESPACES — оттуда же их берёт футер лендинга.
        lazy: async () => ({
          Component: (await import('@/features/support/SupportPage.js')).SupportPage,
        }),
      },
      {
        path: ROUTES.legalUserAgreement,
        lazy: async () => {
          const [module] = await Promise.all([
            import('@/features/legal/LegalDocumentPage.js'),
            ensureNamespaces(PAGE_NAMESPACES.legal),
          ]);
          return { Component: module.LegalDocumentPage };
        },
      },
      {
        /**
         * Инструкция по удалению аккаунта. Тот же компонент и тот же namespace, что у двух
         * юр.документов: страница отличается только содержимым, приходящим с сервера.
         *
         * ⚠️ Адрес открывает проверяющий Google Play напрямую по ссылке из консоли
         * магазина — переименованию он не подлежит (см. ROUTES).
         */
        path: ROUTES.legalAccountDeletion,
        lazy: async () => {
          const [module] = await Promise.all([
            import('@/features/legal/LegalDocumentPage.js'),
            ensureNamespaces(PAGE_NAMESPACES.legal),
          ]);
          return { Component: module.LegalDocumentPage };
        },
      },
      {
        path: ROUTES.legalPrivacyPolicy,
        lazy: async () => {
          const [module] = await Promise.all([
            import('@/features/legal/LegalDocumentPage.js'),
            ensureNamespaces(PAGE_NAMESPACES.legal),
          ]);
          return { Component: module.LegalDocumentPage };
        },
      },
      {
        // Куда возвращают все три браузерных исхода платёжных шлюзов
        // (server/modules/payments/payments.policy.ts:88). Guard'а нет намеренно:
        // пользователь возвращается из внешнего шлюза, и сессия за это время могла
        // истечь — показать результат важнее, чем немедленно увести на вход.
        path: ROUTES.paymentResult,
        lazy: async () => {
          const [module] = await Promise.all([
            import('@/features/payments/PaymentResultPage.js'),
            ensureNamespaces(PAGE_NAMESPACES.paymentResult),
          ]);
          return { Component: module.PaymentResultPage };
        },
      },

      /**
       * Админка (волна 08). ОДИН ленивый вход на всю ветку: оболочка тянет провайдер,
       * бутстрап сессии и хрому, дети — свои страницы. Пользователь, который никогда
       * не открывает /admin, не платит за неё ни байтом начального бандла.
       *
       * ⚠️ Пространство токенов здесь ДРУГОЕ: guard читает админский стор, а не useAuth().
       * `/admin` не входит в RESERVED_PREFIXES сервера, поэтому эти адреса доходят до SPA.
       */
      {
        path: ROUTES.adminSignin,
        lazy: async () => {
          const [module] = await Promise.all([
            import('@/features/admin/AdminShell.js'),
            ensureNamespaces(PAGE_NAMESPACES.admin),
          ]);
          return { Component: module.AdminShell };
        },
        children: [
          {
            lazy: async () => ({
              Component: (await import('@/features/admin/AdminOnlyRoute.js')).AdminGuestRoute,
            }),
            children: [
              {
                index: true,
                lazy: async () => ({
                  Component: (await import('@/features/admin/signin/AdminSignInPage.js'))
                    .AdminSignInPage,
                }),
              },
            ],
          },
          {
            lazy: async () => ({
              Component: (await import('@/features/admin/AdminOnlyRoute.js')).AdminOnlyRoute,
            }),
            children: [
              {
                path: ROUTES.adminDashboard,
                lazy: async () => ({
                  Component: (await import('@/features/admin/dashboard/AdminDashboardPage.js'))
                    .AdminDashboardPage,
                }),
              },
              {
                /**
                 * Аналитика (редизайн, волна 7). Ленивый РЕБЁНОК внутри существующей
                 * админской ветки, а не новый корневой `lazy`: корневой создал бы второй
                 * вход в чанк админки и потянул бы оболочку с провайдером ещё раз.
                 */
                path: ROUTES.adminAnalytics,
                lazy: async () => ({
                  Component: (await import('@/features/admin/analytics/AdminAnalyticsPage.js'))
                    .AdminAnalyticsPage,
                }),
              },
              {
                path: ROUTES.adminUsers,
                lazy: async () => ({
                  Component: (await import('@/features/admin/users/AdminUsersPage.js'))
                    .AdminUsersPage,
                }),
              },
              {
                path: ROUTES.adminTasks,
                lazy: async () => ({
                  Component: (await import('@/features/admin/tasks/AdminTasksPage.js'))
                    .AdminTasksPage,
                }),
              },
              {
                path: ROUTES.adminPriceCards,
                lazy: async () => ({
                  Component: (await import('@/features/admin/plans/AdminPlansPage.js'))
                    .AdminPlansPage,
                }),
              },
              {
                path: ROUTES.adminFinance,
                lazy: async () => ({
                  Component: (await import('@/features/admin/finance/AdminFinancePage.js'))
                    .AdminFinancePage,
                }),
              },
              {
                path: ROUTES.adminCampaigns,
                lazy: async () => ({
                  Component: (await import('@/features/admin/campaigns/AdminCampaignsPage.js'))
                    .AdminCampaignsPage,
                }),
              },
              {
                /**
                 * ⚠️ Ленивый ребёнок ВНУТРИ админской ветки, а не новый корневой `lazy`:
                 * последний завёл бы у админки второй чанк, который тянулся бы отдельно
                 * от её оболочки.
                 */
                path: ROUTES.adminAccountDeletions,
                lazy: async () => ({
                  Component: (await import('@/features/admin/deletions/AdminDeletionsPage.js'))
                    .AdminDeletionsPage,
                }),
              },
              {
                path: ROUTES.adminAudit,
                lazy: async () => ({
                  Component: (await import('@/features/admin/audit/AdminAuditPage.js'))
                    .AdminAuditPage,
                }),
              },
            ],
          },
        ],
      },

      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
